import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import "./radar-chart.css";

const RadarChart = ({ data, variables, width, height, maxValue }) => {
  const svgRef = useRef();

  useEffect(() => {
    // Clear previous renders if any (important for re-rendering when props change)
    d3.select(svgRef.current).selectAll("*").remove();
    drawChart();

    // Cleanup function to remove the external tooltip div when the component unmounts
    return () => {
      d3.select(".d3-tooltip").remove();
    };
  }, [data, variables, width, height, maxValue]);

  const drawChart = () => {
    const margin = { top: 50, right: 150, bottom: 50, left: 50 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    const radius = Math.min(chartWidth, chartHeight) / 2;
    const center = {
      x: chartWidth / 2 + margin.left,
      y: chartHeight / 2 + margin.top,
    };
    const angleSlice = (Math.PI * 2) / variables.length;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    const g = svg
      .append("g")
      .attr("transform", `translate(${center.x}, ${center.y})`);

    const radialScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([0, radius]);

    const color = d3.scaleOrdinal(d3.schemeCategory10);

    // --- Draw the grid lines (concentric circles) and labels ---
    const levelsCount = 5;
    const levelStep = maxValue / levelsCount;

    for (let level = 1; level <= levelsCount; level++) {
      const levelFactor = radius * (level / levelsCount);

      // Draw circle
      g.append("circle")
        .attr("cx", 0)
        .attr("cy", 0)
        .attr("r", levelFactor)
        .style("fill", "#CDCDCD")
        .style("stroke", "#CDCDCD")
        .style("fill-opacity", 0.1);

      // Add text label for the level value
      g.append("text")
        .attr("x", 4) // Offset slightly from center
        .attr("y", -levelFactor + 5) // Position above the circle line
        .attr("class", "level-label")
        .style("font-size", "10px")
        .attr("fill", "#737373")
        .text(`${(level * levelStep).toFixed(0)}`);
    }

    // --- Draw the axis spokes and labels ---
    const axis = g
      .selectAll(".axis")
      .data(variables)
      .enter()
      .append("g")
      .attr("class", "axis");

    axis
      .append("line")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", (d, i) => Math.cos(angleSlice * i - Math.PI / 2) * radius)
      .attr("y2", (d, i) => Math.sin(angleSlice * i - Math.PI / 2) * radius)
      .attr("stroke", "#ccc")
      .attr("stroke-width", 1);

    axis
      .append("text")
      .attr("class", "legend")
      .style("font-size", "11px")
      .attr("text-anchor", "middle")
      .attr(
        "x",
        (d, i) => Math.cos(angleSlice * i - Math.PI / 2) * (radius + 20)
      )
      .attr(
        "y",
        (d, i) => Math.sin(angleSlice * i - Math.PI / 2) * (radius + 20)
      )
      .text((d) => d.label);

    // --- Draw the data areas (blobs) ---
    const radarLine = d3
      .lineRadial()
      .radius((d) => radialScale(d.value))
      .angle((d, i) => i * angleSlice)
      .curve(d3.curveLinearClosed);

    const blobWrapper = g
      .selectAll(".radarWrapper")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "radarWrapper");

    blobWrapper
      .append("path")
      .attr("class", "radarArea")
      .attr("d", (d) =>
        radarLine(
          d.values.map((v, i) => ({ value: v, axis: variables[i].key }))
        )
      )
      .style("fill", (d, i) => color(i))
      .style("fill-opacity", 0.35)
      .style("stroke", (d, i) => color(i))
      .style("stroke-width", 2);
    // Removed mouse events from area to focus interactions on points

    // --- Tooltip Setup (External HTML Div) ---
    // Select the tooltip if it already exists to prevent duplicates on re-render
    let tooltip = d3.select("body").select(".d3-tooltip");
    if (tooltip.empty()) {
      tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "d3-tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("padding", "10px")
        .style("background", "rgba(0, 0, 0, 0.6)")
        .style("color", "#fff")
        .style("border-radius", "4px")
        .style("pointer-events", "none"); // Important: allows mouse events to pass through
    }

    // Mouse event handlers for the points
    function handleMouseOver(event, d) {
      tooltip
        .html(
          `<strong>${d.seriesLabel}</strong><br/>${
            d.axisLabel
          }: ${d.value.toFixed(1)}`
        )
        .style("visibility", "visible");
      d3.select(event.currentTarget).attr("r", 8); // Enlarge point
    }

    function handleMouseOut(event, d) {
      tooltip.style("visibility", "hidden");
      d3.select(event.currentTarget).attr("r", 6); // Revert point size
    }

    d3.select("body").on("mousemove", function (event) {
      tooltip
        .style("top", event.pageY - 10 + "px")
        .style("left", event.pageX + 10 + "px");
    });

    // --- Draw the data points with hover interaction ---
    data.forEach((dataSet, index) => {
      const pointsData = dataSet.values.map((v, i) => ({
        value: v,
        angle: angleSlice * i - Math.PI / 2,
        axisLabel: variables[i].label, // Data needed for tooltip
        seriesLabel: dataSet.label, // Data needed for tooltip
      }));

      g.selectAll(`.series-point-${index}`)
        .data(pointsData)
        .enter()
        .append("circle")
        .attr("r", 6) // Radius of the point
        .attr("cx", (d) => Math.cos(d.angle) * radialScale(d.value))
        .attr("cy", (d) => Math.sin(d.angle) * radialScale(d.value))
        .style("fill", color(index))
        .style("stroke", "#fff")
        .style("cursor", "pointer")
        .on("mouseover", handleMouseOver)
        .on("mouseout", handleMouseOut);
    });

    // --- Add a Legend ---
    const legend = svg
      .append("g")
      .attr("class", "legend")
      // Position the legend at the top-left (X: 10px, Y: 10px within the SVG)
      .attr("transform", `translate(10, 10)`);

    const legendItem = legend
      .selectAll(".legend-item")
      .data(data)
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => `translate(0, ${i * 25})`); // Vertical spacing

    legendItem
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 12)
      .attr("height", 12)
      .style("fill", (d, i) => color(i));

    legendItem
      .append("text")
      .attr("x", 20)
      .attr("y", 10)
      .attr("class", "legend-text")
      .style("font-size", "12px")
      .text((d) => d.label)
      .attr("text-anchor", "start")
      .style("alignment-baseline", "middle");
  };

  return (
    <div>
      <svg ref={svgRef}></svg>
    </div>
  );
};

export default RadarChart;
